import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import MediaLightbox from '../components/MediaLightbox';
import { getAbout } from '../services/api';
import { Download, Sparkles, ArrowRight } from 'lucide-react';

const About = () => {
  const [aboutContent, setAboutContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const data = await getAbout();
      setAboutContent(data);
    } catch (err) {
      console.error('Error fetching about:', err);
    } finally {
      setLoading(false);
    }
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    const additionalImages = aboutContent?.additional_images || [];
    setLightboxIndex((prev) => (prev === 0 ? additionalImages.length - 1 : prev - 1));
  };

  const goToNext = () => {
    const additionalImages = aboutContent?.additional_images || [];
    setLightboxIndex((prev) => (prev === additionalImages.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading...</p>
        </div>
      </div>
    );
  };

  const paragraphs = aboutContent?.content ? aboutContent.content.split('\n\n') : [];
  const additionalImages = aboutContent?.additional_images || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <SEO
        title="About Charles Kilvington | Video Editor & Motion Graphics Artist"
        description="London-born creative professional with expertise in video editing, motion graphics, and street art. From Los Angeles to Las Vegas, discover the journey of an innovative visual storyteller."
        keywords="Charles Kilvington, video editor biography, motion graphics artist, cinematographer, street artist, creative professional"
        ogTitle="About Charles Kilvington | Video Editor & Motion Graphics Artist"
        ogDescription="London-born creative professional with expertise in video editing, motion graphics, and street art."
        ogUrl="https://charleskilvington.com/about"
        ogImage="https://charleskilvington.com/og-image.jpg"
        twitterTitle="About Charles Kilvington"
        twitterDescription="London-born creative professional with expertise in video editing, motion graphics, and street art."
        twitterImage="https://charleskilvington.com/og-image.jpg"
      />
      <Header />

      <main className="pt-28 md:pt-36 pb-20 px-6 md:px-12">
        {/* Hero Section */}
        <div className="max-w-[1400px] mx-auto mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative">
            {/* Left: Title & Content - Centered */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
              className="space-y-8 relative z-10 flex flex-col items-center lg:items-start text-center lg:text-left"
            >
              <div className="inline-block">
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.1, duration: 0.4 }}
                  className="flex items-center gap-3 mb-4 justify-center lg:justify-start"
                >
                  <Sparkles className="text-purple-400" size={24} />
                  <span className="text-purple-400 font-medium tracking-wider uppercase text-sm">About Me</span>
                </motion.div>

                <motion.h1
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
                  className="text-5xl md:text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-200 to-pink-200 leading-tight mb-6 pb-2 text-center lg:text-left"
                >
                  {aboutContent?.title || 'Charles Kilvington'}
                </motion.h1>
                <motion.div
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: 0.3, duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
                  className="h-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full mx-auto lg:mx-0"
                  style={{ transformOrigin: 'center', width: 96 }}
                />
              </div>

              {/* Quote with glass effect */}
              {aboutContent?.quote && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
                  className="relative pl-6 border-l-4 border-purple-500/50 backdrop-blur-sm bg-white/5 p-6 rounded-r-xl w-full"
                >
                  <p className="text-xl md:text-2xl text-gray-300 font-light italic leading-relaxed">
                    "{aboutContent.quote}"
                  </p>
                </motion.div>
              )}

              {/* Resume Download - Centered */}
              {aboutContent?.resume_url && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5, duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
                >
                  <a
                    href={aboutContent.resume_url.startsWith('http') ? aboutContent.resume_url : `${process.env.REACT_APP_BACKEND_URL}${aboutContent.resume_url}`}
                    download="Charles_Kilvington_Resume.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 font-semibold shadow-2xl shadow-purple-500/30 hover:scale-105 hover:shadow-purple-500/50"
                  >
                    <Download size={20} />
                    Download Resume
                  </a>
                </motion.div>
              )}
            </motion.div>

            {/* Right: Hero Image */}
            {aboutContent?.image_url && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1], delay: 0.15 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500"></div>
                <div className="relative overflow-hidden rounded-3xl border border-purple-500/20 shadow-2xl">
                  <img
                    src={aboutContent.image_url}
                    alt="Charles Kilvington"
                    className="w-full h-[400px] lg:h-[600px] object-cover transform group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                </div>
              </motion.div>
            )}
          </div>
        </div>

        {/* Story Section with Scroll Animations */}
        <div className="max-w-[1000px] mx-auto space-y-16">
          {/* The Story Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
            className="text-3xl md:text-4xl font-bold text-white text-center"
          >
            The Story
          </motion.h2>

          {/* Main Content with Floating Images */}
          <div className="relative">
            <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
              {paragraphs.map((paragraph, index) => {
                const showImage = index % 2 === 0;
                const imageIndex = Math.floor(index / 2);
                const hasImage = showImage && imageIndex < additionalImages.length;
                // First image left, second right, third left, etc.
                const imagePosition = imageIndex % 2 === 0 ? 'left' : 'right';

                // Special case: 3rd image (imageIndex 2) shows one paragraph lower on mobile
                const hasMobileImage = hasImage && imageIndex !== 2;
                const showThirdImageOnMobile = index === 5 && additionalImages.length > 2;

                return (
                  <div key={index} className="relative">
                    {/* Desktop: Show floating images */}
                    {hasImage && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "0px" }}
                        transition={{ duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
                        className={`
                          hidden md:block
                          ${imagePosition === 'right' ? 'float-right ml-8 mb-6' : 'float-left mr-8 mb-6'}
                          w-[350px] group cursor-pointer
                        `}
                        onClick={() => openLightbox(imageIndex)}
                      >
                        <div className="relative">
                          <div className="absolute inset-0 bg-gradient-to-r from-purple-500/30 to-pink-500/30 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                          <div className="relative overflow-hidden rounded-2xl border border-purple-500/20">
                            <img
                              src={additionalImages[imageIndex]}
                              alt={`Story ${imageIndex + 1}`}
                              className="w-full h-auto transform group-hover:scale-110 transition-transform duration-700"
                            />
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Mobile: Show images inline between paragraphs (except 3rd image) */}
                    {hasMobileImage && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "0px" }}
                        transition={{ duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
                        className="md:hidden relative group mb-6 cursor-pointer"
                        onClick={() => openLightbox(imageIndex)}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl"></div>
                        <div className="relative overflow-hidden rounded-2xl border border-purple-500/20">
                          <img
                            src={additionalImages[imageIndex]}
                            alt={`Story ${imageIndex + 1}`}
                            className="w-full h-auto"
                          />
                        </div>
                      </motion.div>
                    )}

                    {/* Mobile: Show 3rd image at paragraph 5 */}
                    {showThirdImageOnMobile && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, margin: "0px" }}
                        transition={{ duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
                        className="md:hidden relative group mb-6 cursor-pointer"
                        onClick={() => openLightbox(2)}
                      >
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl"></div>
                        <div className="relative overflow-hidden rounded-2xl border border-purple-500/20">
                          <img
                            src={additionalImages[2]}
                            alt={`Story 3`}
                            className="w-full h-auto"
                          />
                        </div>
                      </motion.div>
                    )}

                    <motion.p
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "0px" }}
                      transition={{ duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
                      className="mb-6"
                    >
                      {paragraph}
                    </motion.p>
                  </div>
                );
              })}
              <div className="clear-both"></div>
            </div>
          </div>

          {/* Expertise Section */}
          {aboutContent?.highlights && aboutContent.highlights.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-16"
            >
              {/* Dividing Line */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
                className="flex items-center gap-4 my-12"
              >
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
                  className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"
                  style={{ transformOrigin: 'left' }}
                />
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
                  className="flex items-center gap-3"
                >
                  <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                  <div className="h-2 w-2 rounded-full bg-pink-500"></div>
                  <div className="h-2 w-2 rounded-full bg-purple-500"></div>
                </motion.div>
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
                  className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"
                  style={{ transformOrigin: 'right' }}
                />
              </motion.div>

              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-3xl md:text-4xl font-bold text-white mb-10 text-center"
              >
                Expertise & Skills
              </motion.h2>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "0px" }}
                transition={{
                  duration: 0.5,
                  ease: [0.25, 0.4, 0.25, 1]
                }}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto"
              >
                {aboutContent.highlights.map((skill, index) => (
                  <div
                    key={index}
                    className="group relative rounded-xl p-[1px] bg-gradient-to-r from-purple-500/50 to-pink-500/50 hover:from-purple-500 hover:to-pink-500 transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="bg-[#1a1a2e] rounded-xl p-5 h-full flex items-center justify-center min-h-[60px]">
                      <p className="text-gray-200 font-medium text-center group-hover:text-white transition-colors">
                        {skill}
                      </p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* Dividing Line */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
            className="flex items-center gap-4 my-20"
          >
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
              className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"
              style={{ transformOrigin: 'left' }}
            />
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
              className="flex items-center gap-3"
            >
              <div className="h-2 w-2 rounded-full bg-purple-500"></div>
              <div className="h-2 w-2 rounded-full bg-pink-500"></div>
              <div className="h-2 w-2 rounded-full bg-purple-500"></div>
            </motion.div>
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.25, 0.4, 0.25, 1] }}
              className="flex-1 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"
              style={{ transformOrigin: 'right' }}
            />
          </motion.div>

          {/* Call to Action Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-purple-500/10 via-pink-500/10 to-purple-500/10 border border-purple-500/20 px-6 md:px-16 py-12 md:py-16">
              {/* Gradient blur effects */}
              <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl"></div>

              <div className="relative z-10 text-center">
                <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-white via-purple-200 to-pink-200 mb-4 md:mb-6 pb-2">
                  Let's Create Something Amazing
                </h2>

                <p className="text-base md:text-xl text-gray-300 mb-8 md:mb-8 max-w-2xl mx-auto">
                  Ready to bring your vision to life? Get in touch and let's discuss your next project.
                </p>

                <button
                  onClick={() => navigate('/contact')}
                  className="group inline-flex items-center gap-2 md:gap-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 md:px-10 md:py-5 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 font-bold text-base md:text-lg shadow-2xl shadow-purple-500/30 hover:scale-105 hover:shadow-purple-500/50 mb-12 md:mb-0"
                >
                  Get In Touch
                  <ArrowRight className="transition-transform duration-300 group-hover:translate-x-2" size={20} />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />

      {/* Media Lightbox */}
      {lightboxOpen && (
        <MediaLightbox
          media={additionalImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrevious={goToPrevious}
          onNext={goToNext}
        />
      )}
    </div>
  );
};

export default About;
